/** The question bank controller is responsible for manipulating the question bank. */
import {attachDropdown} from '@/main'
import {assertNotNull} from '@/util'
import {sortElementsByDataAttributes} from '@/sort_selector'

class QuestionBankController {
  static readonly FILTER_ID = 'question-bank-filter'
  static readonly QUESTION_CLASS = 'cf-question-bank-element'
  static readonly OPEN_QUESTION_BANK_BUTTON = 'cf-open-question-bank-button'
  static readonly CLOSE_QUESTION_BANK_BUTTON = 'cf-close-question-bank-button'
  static readonly QUESTION_BANK_CONTAINER = 'cf-question-bank-container'
  static readonly QUESTION_BANK_HIDDEN = 'cf-question-bank-hidden'
  static readonly BANK_SHOWN_URL_PARAM = 'sqb'
  static readonly RELEVANT_FILTER_TEXT_DATA_ATTR = 'data-relevantfiltertext'

  static readonly SORT_SELECT_ID = 'question-bank-sort'
  static readonly QUESTION_BANK_FORM_ID = 'question-bank-panel-form'
  static readonly CREATE_QUESTION_BUTTON_ID = 'create-question-button'

  constructor() {
    QuestionBankController.attachFilterAndSortListeners()
    QuestionBankController.initToggleQuestionBankButtons()
    QuestionBankController.attachCloseButtonListener()
    QuestionBankController.initOpenOnHtmxTrigger()
    QuestionBankController.initCloseOnHtmxTrigger()
    QuestionBankController.rebindCreateQuestionDropdownOnHtmxSwap()

    // Apply a filter value the browser may have restored at page load (e.g. Firefox refresh after
    // the user typed in the filter input). Guarded so we don't loop through the questions when
    // there's nothing to filter against.
    const filterInput = document.getElementById(
      QuestionBankController.FILTER_ID,
    ) as HTMLInputElement | null
    if (filterInput && filterInput.value.length > 0) {
      QuestionBankController.filterQuestions()
    }
  }

  /**
   * Attach the filter input and sort selector listeners to document, so they fire on any
   * matching input/change event regardless of which element bubbled it up. The
   * bank's form is replaced (outerHTML swap) every time a special-mode open-bank button fires,
   * so we need document-level listeners if we want the listeners to survive swapping.
   */
  private static attachFilterAndSortListeners() {
    document.addEventListener('input', (event) => {
      if (
        (event.target as HTMLElement | null)?.id ===
        QuestionBankController.FILTER_ID
      ) {
        QuestionBankController.filterQuestions()
      }
    })
    document.addEventListener('change', (event) => {
      if (
        (event.target as HTMLElement | null)?.id ===
        QuestionBankController.SORT_SELECT_ID
      ) {
        QuestionBankController.sortQuestions()
      }
    })
  }

  private static initToggleQuestionBankButtons() {
    // To animate question bank we need to use a hack. Question bank is initially
    // hidden using tailwinds `hidden` class: `display: none`. When user clicks
    // "add question" button we want to display questyion bank using quick animation.
    // But CSS transitions don't work if they are triggered simultaneously with display:
    // https://stackoverflow.com/questions/3331353/transitions-on-the-css-display-property
    // To workaround we use 2 classes: `hidden` and `cf-question-bank-hidden`.
    //
    // When showing question we first remove `hidden` and wait for browser to apply it by
    // using requestAnimationFrame() and then remove `cf-question-bank-hidden` class.
    //
    // When hiding we first add `cf-question-bank-hidden` to trigger transition and once
    // it's over add `hidden` class.
    //
    // CSS is fun!
    const questionBankContainer = document.getElementById(
      QuestionBankController.QUESTION_BANK_CONTAINER,
    )
    if (questionBankContainer == null) {
      return
    }

    const openQuestionBankElements = Array.from(
      document.getElementsByClassName(
        QuestionBankController.OPEN_QUESTION_BANK_BUTTON,
      ),
    )
    for (const element of openQuestionBankElements) {
      element.addEventListener('click', () => {
        QuestionBankController.showQuestionBank(questionBankContainer)
      })
    }
    if (!questionBankContainer.classList.contains('hidden')) {
      QuestionBankController.makeBodyNonScrollable()
    }
  }

  /**
   * Close the question bank on a click on any close control — the glasspane or the X button.
   * Delegated on the container (which is NOT part of the swapped panel form) rather than binding
   * the close buttons directly, so the listener survives the HTMX outerHTML swap that replaces the
   * X button in initial-question mode. closest() also handles clicks landing on the X button's
   * inner SVG.
   */
  private static attachCloseButtonListener() {
    const container = document.getElementById(
      QuestionBankController.QUESTION_BANK_CONTAINER,
    )
    if (container == null) {
      return
    }
    container.addEventListener('click', (event) => {
      const closeControl = (event.target as HTMLElement | null)?.closest(
        '.' + QuestionBankController.CLOSE_QUESTION_BANK_BUTTON,
      )
      if (closeControl != null) {
        QuestionBankController.hideQuestionBank(container)
      }
    })
  }

  static showQuestionBank(container: HTMLElement) {
    QuestionBankController.makeBodyNonScrollable()
    container.classList.remove('hidden')
    window.requestAnimationFrame(() => {
      container.classList.remove(QuestionBankController.QUESTION_BANK_HIDDEN)
    })
    const url = new URL(location.href)
    url.searchParams.set(QuestionBankController.BANK_SHOWN_URL_PARAM, 'true')
    window.history.replaceState({}, '', url.toString())
  }

  static hideQuestionBank(container: HTMLElement) {
    const panel = assertNotNull(
      container.querySelector('.cf-question-bank-panel'),
    )
    panel.addEventListener(
      'transitionend',
      () => {
        container.classList.add('hidden')
        QuestionBankController.makeBodyScrollable()
      },
      {once: true},
    )
    container.classList.add(QuestionBankController.QUESTION_BANK_HIDDEN)
    const url = new URL(location.href)
    url.searchParams.delete(QuestionBankController.BANK_SHOWN_URL_PARAM)
    window.history.replaceState({}, '', url.toString())
  }

  /**
   * Opens the question bank when the server sends an `HX-Trigger-After-Swap: openQuestionBank`
   * header. The special-mode open-bank buttons (initial-question, choose-existing) rely on this
   * so the bank slides in only after the HTMX swap has completed, ensuring the panel is in the
   * requested mode before the user sees it.
   */
  private static initOpenOnHtmxTrigger() {
    document.addEventListener('openQuestionBank', () => {
      const container = document.getElementById(
        QuestionBankController.QUESTION_BANK_CONTAINER,
      )
      if (container) {
        QuestionBankController.showQuestionBank(container)
      }
    })
  }

  /** Closes the question bank when the server sends an `HX-Trigger-After-Swap: closeQuestionBank` header. */
  private static initCloseOnHtmxTrigger() {
    document.addEventListener('closeQuestionBank', () => {
      const container = document.getElementById(
        QuestionBankController.QUESTION_BANK_CONTAINER,
      )
      if (container) {
        QuestionBankController.hideQuestionBank(container)
      }
    })
  }

  /**
   * Re-binds the "Create new question" dropdown trigger after an HTMX swap of the question bank form.
   * The swap of the form replaces the button with a fresh one that has no click handler.
   * `attachDropdown` from main.ts runs once at page load against the original button;
   * here we re-run it against the post-swap button.
   */
  private static rebindCreateQuestionDropdownOnHtmxSwap() {
    document.body.addEventListener('htmx:afterSwap', (e) => {
      if (e.detail.target.id === QuestionBankController.QUESTION_BANK_FORM_ID) {
        attachDropdown(QuestionBankController.CREATE_QUESTION_BUTTON_ID)
      }
    })
  }

  static makeBodyNonScrollable() {
    // When the question bank is visible, only the bank should be scrollable. Body
    // and all other elements on the page should be non-scrollable.
    // Using https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior
    // doesn't work as body is still scrollable when scrolling over glasspane.
    document.body.classList.add('overflow-y-hidden')
  }

  static makeBodyScrollable() {
    document.body.classList.remove('overflow-y-hidden')
  }

  /**
   * Filter questions in the question bank with the filter input string, on the question name and description.
   */
  static filterQuestions() {
    const filterString = (
      document.getElementById(
        QuestionBankController.FILTER_ID,
      ) as HTMLInputElement
    ).value.toUpperCase()
    const questions = Array.from(
      document.getElementsByClassName(QuestionBankController.QUESTION_CLASS),
    )
    questions.forEach((question) => {
      const questionElement = question as HTMLElement
      const questionFilterText =
        questionElement.getAttribute(
          QuestionBankController.RELEVANT_FILTER_TEXT_DATA_ATTR,
        ) ?? questionElement.innerText
      questionElement.classList.toggle(
        'hidden',
        filterString.length > 0 &&
          !questionFilterText.toUpperCase().includes(filterString),
      )
    })
  }

  /**
   * Sort questions in the question bank based on the criteria selected from the dropdown.
   */
  private static sortQuestions() {
    sortElementsByDataAttributes(
      QuestionBankController.SORT_SELECT_ID,
      '.cf-sortable-questions',
      '.' + QuestionBankController.QUESTION_CLASS,
    )
  }
}

export function init() {
  new QuestionBankController()
}
